import { Component, HostListener, Input, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, debounceTime, filter, firstValueFrom, Subject, takeUntil, timer } from 'rxjs';
import { eBusinessSearchCriteriaTypeString, eOrderStatusType, eOrderType, ePartyType } from '../../enums';
import { Order } from '../../interfaces/orders/order';
import cytoscape, { ElementsDefinition, NodeDefinition, NodeSingular, SingularElementReturnValue, StylesheetJson } from 'cytoscape';
import dagre from 'cytoscape-dagre';
import { Utilities } from '../../utilities';
import { SearchResultsRepositoryService } from '../../services/search-results/search-results-repository.service';
import { base64StringToBlob } from 'blob-util';
import { Party } from '../../interfaces/parties/party';
import { PartyUIConfiguration } from '../../interfaces/ui-configurations/party-ui-configuration';
import { ParsedBusinessReportEntity } from '../../interfaces/business-report-parsed-results/parsed-business-report-entity';
import { OrderRepositoryService } from '../../services/order/order-repository-service.service';
import { MatDialog } from '@angular/material/dialog';
import { UltimateBeneficialOwnershipDialogComponent } from '../ultimate-beneficial-ownership-dialog/ultimate-beneficial-ownership-dialog.component';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { OrderUIConfiguration } from '../../interfaces/ui-configurations/order-ui-configuration';
import { UIConfigurationService } from '../../services/ui-configurations/ui-configuration.service';
import { DatePipe } from '@angular/common';
import { HubReport } from '../../interfaces/hub-reports/hub-report';
import { UboTreeNode } from '../../interfaces/hub-reports/ubo-tree-node';
import { UboTreeNodesService } from '../../services/ubo-tree-nodes/ubo-tree-nodes.service';
import { UboOwnershipRecord } from '../../interfaces/ubo/ownership-record';
import { UboOwnershipLevel } from '../../interfaces/ubo/ownership-level';
import { SearchResult } from '../../interfaces/search-results/search-result';
import { HubReportsService } from '../../services/hub-reports/hub-reports.service';
import { TranslocoService } from '@jsverse/transloco';
import { EnumLabelService } from '../../services/enum-label/enum-label.service';

export interface UboTreeNodeTab {
  label: string;
  node: UboTreeNode;
  businessSearchOrder?: Order;
  businessReportOrder?: Order;
  uboOrder?: Order;
  pepsAndSanctionsOrder?: Order;
  someParentHasIncompleteUBO: boolean;
  someChildHasUBO: boolean;
}

@Component({
  selector: 'lib-hub-report-v2',
  templateUrl: './hub-report-v2.component.html',
  styleUrl: './hub-report-v2.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class HubReportV2Component implements OnInit, OnDestroy {
  private readonly enumLabels = inject(EnumLabelService);
  private readonly transloco = inject(TranslocoService);
  @Input() public isAdmin: boolean = false;

  protected hubReportID?: string;
  protected hubReport?: HubReport;
  protected levels?: UboOwnershipLevel[];
  protected finalOwnershipRecords?: UboOwnershipRecord[];
  protected orders: Order[] = [];
  protected outstandingOwnership?: number;
  protected lastUpdatedDate?: Date;

  protected currentRoot?: UboTreeNode;
  protected tabs: UboTreeNodeTab[] = [];
  protected tabIndex: number = 0;

  protected selectedNode?: any;
  protected actionableNodes: string[] = [];

  protected businessReportResults = new Map<string, BehaviorSubject<ParsedBusinessReportEntity | undefined>>();
  protected pepsAndSanctionsParties$ = new BehaviorSubject<Party[]>([]);
  protected searchResults = new Map<string, SearchResult | null | undefined>();

  protected loading = false;
  protected hasPepsAndSanctions = false;
  protected uboOrderConfiguration?: OrderUIConfiguration;

  private onDestroy$ = new Subject<void>();
  private onResize$ = new Subject<void>();

  protected cytoscapeTree = new Subject<ElementsDefinition>();
  private cy!: cytoscape.Core;
  private layout = {
    name: 'dagre',
    rankDir: 'TB',
    rankSep: 40,
    edgeSep: 6,
    ranker: 'tight-tree',
    nodeDimensionsIncludeLabels: true
  } as cytoscape.LayoutOptions;

  private get primaryColor(): string {
    return getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim() || '#0072AE';
  }

  constructor(
    private route: ActivatedRoute,
    private searchResultsRepo: SearchResultsRepositoryService,
    private router: Router,
    private ordersRepo: OrderRepositoryService,
    private dialog: MatDialog,
    private uiConfigurationService: UIConfigurationService,
    private datePipe: DatePipe,
    private uboTreeNodeSerivce: UboTreeNodesService,
    private hubReportService: HubReportsService) { }

  ngOnInit(): void {
    this.loading = true;

    this.route.paramMap.pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(params => {
      this.hubReportID = params.get('hubReportID')!;
    });

    this.hubReportService.getHubReportWithUboTrees(this.hubReportID!).subscribe(report => {
      this.hubReport = report;
      this.currentRoot = this.hubReport.uboTrees.at(this.hubReport.uboTrees.length - 1)!;

      this.uboTreeNodeSerivce.getOwnershipLevelsForNode(this.currentRoot.id).subscribe(levels => {
        this.finalOwnershipRecords = this.getFinalOwnershipRecords(levels);
      });

      this.uboTreeNodeSerivce.getOrdersForNode(this.currentRoot.id).subscribe(orders => {
        this.orders = orders;
        const pepsAndSanctionsParties: Party[] = [];

        this.walkTree(this.currentRoot, (node, parent) => {
          node.hubReport = this.hubReport;
          node.parent = parent;
          let tab: UboTreeNodeTab;

          if (node.party?.partyTypeID === ePartyType.businessShareholder) {
            let someParentHasIncompleteUBO = false;
            let current = node.parent;

            while (current) {
              someParentHasIncompleteUBO = current.orders.some(o => 
                o.orderTypeID === eOrderType.ultimateBeneficialOwnershipReport &&
                o.orderStatusTypeID !== eOrderStatusType.complete);
                
              if (someParentHasIncompleteUBO === true) {
                break;
              }

              current = current.parent;
            }

            tab = {
              label: this.getNodeLabel(node, false, false, true),
              node: node,
              someParentHasIncompleteUBO: someParentHasIncompleteUBO,
              someChildHasUBO: this.doesDownstreamShareholderHaveUBO(node)
            };

            const businessSearchOrderMin = node.orders.find(o => o.orderTypeID === eOrderType.businessSearch);
            const businessReportOrderMin = node.orders.find(o => o.orderTypeID === eOrderType.businessReport);
            const uboOrderMin = node.orders.find(o => o.orderTypeID === eOrderType.ultimateBeneficialOwnershipReport);
            const pepsAndSanctionsorderMin = node.orders.find(o => o.orderTypeID === eOrderType.pepsAndSanctions);

            if (businessSearchOrderMin) {
              tab.businessSearchOrder = orders.find(o => o.id === businessSearchOrderMin.id);
            }

            if (businessReportOrderMin) {
              tab.businessReportOrder = orders.find(o => o.id === businessReportOrderMin.id);

              if (tab.businessReportOrder) {
                this.searchResults.set(tab.businessReportOrder.id, tab.businessReportOrder.searchResult);

                if (tab.businessReportOrder.orderStatusTypeID === eOrderStatusType.complete &&
                  tab.businessReportOrder.searchResult?.hasParsedResult) {
                  const entitySubject$ = new BehaviorSubject<ParsedBusinessReportEntity | undefined>(undefined);
                  this.businessReportResults.set(tab.businessReportOrder.id, entitySubject$); 
                  this.searchResultsRepo.getBusinessReportParsedResults(tab.businessReportOrder.id).subscribe(results => entitySubject$.next(results));
                }
              }
            }

            if (uboOrderMin) {
              tab.uboOrder = orders.find(o => o.id === uboOrderMin.id);

              this.uiConfigurationService.getOrderUIConfigurationByValues({
                country: uboOrderMin.country ?? 'CA',
                jurisdiction: this.hubReport?.jurisdiction ?? uboOrderMin.jurisdiction ?? '',
                orderTypeID: eOrderType.ultimateBeneficialOwnershipReport
              }).subscribe(config => this.uboOrderConfiguration = config); 
            }

            if (pepsAndSanctionsorderMin) {
              tab.pepsAndSanctionsOrder = orders.find(o => o.id === pepsAndSanctionsorderMin.id);
              this.hasPepsAndSanctions = true;

              if (tab.pepsAndSanctionsOrder?.parties) {
                for(let i = 0; i < tab.pepsAndSanctionsOrder.parties.length; i++) {
                  const outer = tab.pepsAndSanctionsOrder.parties[i];
                  if (!pepsAndSanctionsParties.some(inner => `${outer.firstName?.trim()}${outer.middleName?.trim()}${outer.lastName?.trim()}${outer.busName?.trim()}${outer.busNumber?.trim()}` ===
                    `${inner.firstName?.trim()}${inner.middleName?.trim()}${inner.lastName?.trim()}${inner.busName?.trim()}${inner.busNumber?.trim()}`)) {
                    pepsAndSanctionsParties.push(outer);
                  }
                }
              }
            }

            this.tabs.push(tab); 
          }

          for (let i = 0; i < node.orders?.length; i++) {
            const order = node.orders.at(i);

            if (!order) {
              continue;
            }

            if (order.orderStatusUpdatedDate) {
              const date = new Date(order.orderStatusUpdatedDate);

              if (!this.lastUpdatedDate || date > this.lastUpdatedDate) {
                this.lastUpdatedDate = date;
              }
            }
          }
        });

        this.pepsAndSanctionsParties$.next(pepsAndSanctionsParties);

        this.initCytoscape();

        const tree: ElementsDefinition = { nodes: [], edges: [] };
        this.initializeCytoscapeTree(tree, this.currentRoot); 
        this.cytoscapeTree.next(tree);

        this.loading = false;
      });
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.hubReportService.currentHubReport$.next(undefined);
  }

  @HostListener('window:resize')
  public onResize(): void {
    this.onResize$.next();
  }

  public downloadHubReport() {
    if (!this.currentRoot?.document) {
      return;
    }

    this.uboTreeNodeSerivce.getNodeDocument(this.currentRoot.id, this.currentRoot?.document).subscribe(doc => {
      const blobUrl = URL.createObjectURL(doc);
      window.open(blobUrl, '_blank');
    });
  }
  
  private initCytoscape() {
    const cytoscapeTreePromise = firstValueFrom(this.cytoscapeTree.pipe(
      filter(cytoscapeTree => !!cytoscapeTree)));

    cytoscape.use(dagre);
    this.cy = cytoscape({
      container: document.getElementById('ubo_tree'),
      elements: cytoscapeTreePromise,
      style: this.buildStyle(),
      layout: this.layout,
      boxSelectionEnabled: false,
      minZoom: 0.1
    });

    this.cy.ready(() => {
      if (this.getTreeDepth(this.currentRoot) < 3) {
        this.cy.zoom(1.5);
      } else {
        this.cy.fit(undefined, 15);
      }
      this.cy.center();

      this.cy.nodes().forEach(node => {
        const nodeWidth = this.getLabelWidth(node);
        node.style({ width: nodeWidth });
      });

      for (let i = 0; i < this.actionableNodes.length; i++) {
        const id = this.actionableNodes.at(i);
        const pulseLoop = (pulse: boolean) => {
          const node = this.cy.nodes().$id(id!);

          node?.animate({
            style: { 'background-color': pulse ? '#C6DBEA' : this.primaryColor }
          },
          {
            duration: 1250,
            complete: () => timer(pulse ? 0 : 1500).subscribe(() => {
              if (this.actionableNodes.find(inner => inner === id)) {
                pulseLoop(!pulse);
              }
            })
          });
        };

        pulseLoop(true);
      }
    });

    this.cy.on('tap', 'node', event => {
      let node = this.cy.$id(event.target.id());
      
      // ctrl clicking a node will copy the id of the ubo tree node to your clipboard
      // handy during development and QA
      if (event.originalEvent.ctrlKey || event.originalEvent.metaKey) {
        navigator.clipboard.writeText(node.id())
          .then(() => console.log(node.id()))
          .catch(err => console.error('Failed:', err));
      } else {
        this.onSelectionChange(node, true);
      }
    });

    this.cy.on('tap', event => {
      // on background click, unhighlight everythin
      if (event.target === this.cy) {
        this.selectedNode = undefined;

        this.cy.elements().forEach(n => {
          n.removeClass('selected-node');
          n.removeClass('highlighted-edge');
        });
      }
    });

    // cxttap is right click, reset the graph on right click
    this.cy.on('cxttap', event => {
      if (event.target === this.cy) {
        if (this.getTreeDepth(this.currentRoot) < 3) {
          this.cy.zoom(1.5);
        } else {
          this.cy.fit(undefined, 15);
        }
        this.cy.center();
      }
    });

    this.cy.on('mouseover', 'node', event => {
      const node = event.target;

      if (!this.selectedNode) {
        this.walkCytoscapeTreeEdges(node.id(), edge => {
          edge.addClass('highlighted-edge');
        });
      }
    });

    this.cy.on('mouseout', 'node', event => {
      const node = event.target;

      if (!this.selectedNode) {
        this.walkCytoscapeTreeEdges(node.id(), edge => {
          edge.removeClass('highlighted-edge');
        });
      }
    });

    this.onResize$.pipe(
      takeUntil(this.onDestroy$),
      debounceTime(250))
    .subscribe(() => {
      this.cy.center();
    });
  }

  private initializeCytoscapeTree(tree: ElementsDefinition, root?: UboTreeNode) {
    if (!root) {
      return;
    }

    const rootNode: NodeDefinition = {
      data: { id: root.id, label: this.getNodeLabel(root, false, this.currentRoot?.id !== root.id) },
      classes: 'center-center'
    };

    // it's actionable if it's a business shareholder node, AND
    // if there are no business reports that are complete or pending OR
    // it is unregistered and there are no shareholders
    if (root.party.partyTypeID === ePartyType.businessShareholder &&
      ((root.isValidated &&
        !root.isUnregisteredEntity &&
        !root.orders.some(o => o.orderTypeID === eOrderType.businessReport &&
        (o.orderStatusTypeID === eOrderStatusType.complete ||
          o.orderStatusTypeID === eOrderStatusType.pending))) ||
        (root.isUnregisteredEntity &&
          root.children.length === 0 &&
          !(root.hasDeclaredNoSignificantShareholders ?? false)))) {
      this.actionableNodes.push(root.id);
    }

    for (let i = 0; i < root.children.length; i++) {
      const child = root.children[i];
      this.initializeCytoscapeTree(tree, child);

      tree.edges.push({
        data: { source: root.id, target: child.id },
        classes: 'round-taxi'
      });
    }

    tree.nodes.push(rootNode);
  }

  private getTreeDepth(root?: UboTreeNode): number {
    if (!root) {
      return 0;
    }

    if (!root.children || root.children.length === 0) {
      return 1;
    } else {
      return 1 + Math.max(...root.children.map(child => this.getTreeDepth(child)));
    }
  }  

  protected onSelectedTabChange(event: MatTabChangeEvent) {
    let index = event.index;
    index -= this.hasPepsAndSanctions ? 2 : 1;

    if (index >= 0) {
      const tab = this.tabs.at(index);

      if(tab) {
        const node = this.cy.nodes().$id(tab.node.id);
        if (this.selectedNode !== node) {
          this.onSelectionChange(node, false);
        }
      }
    } else {
      this.selectedNode = undefined;
    }

    this.scroll();
  }
  
  private onSelectionChange(node: cytoscape.CollectionReturnValue | NodeSingular, updateIndex: boolean) {
    this.cy.elements('node').forEach(n => {
      n.removeClass('selected-node');
    });

    if (this.selectedNode === node) {
      this.selectedNode = undefined;
    } else {
      this.cy.elements('edge').forEach(edge => {
        edge.removeClass('highlighted-edge');
      });

      const uboTreeNode = this.getUboTreeNodeByID(node.id());

      if (uboTreeNode?.party.partyTypeID === ePartyType.individualShareholder && uboTreeNode.parentID) {
        const newNode = this.cy.$id(uboTreeNode.parentID);

        if (newNode) {
          node = newNode;
        }
      } 

      node.addClass('selected-node');
      this.selectedNode = node;

      if (updateIndex) {
        let index = this.tabs.findIndex(tab => tab.node.id === node.id());

        if (index !== -1) {
          index += this.hasPepsAndSanctions ? 2: 1;
          this.tabIndex = index;
        } 
      }

      this.walkCytoscapeTreeEdges(node.id(), edge => edge.addClass('highlighted-edge'));
    }

    const actionableNodeEntryIndex = this.actionableNodes.findIndex(id => node.id() === id);

    if (actionableNodeEntryIndex !== -1) {
      this.actionableNodes.splice(actionableNodeEntryIndex, 1);
      node.stop();
    }

    node?.style({
      'background-color': this.primaryColor
    });
  }

  private walkTree(root: UboTreeNode | null | undefined, action: (node: UboTreeNode, parent?: UboTreeNode) => void, parent?: UboTreeNode) {
    if(!root) {
      return;
    }

    action(root, parent);

    for(let i = 0; i < root.children?.length; i++) {
      this.walkTree(root.children.at(i), action, root);
    }
  }

  private walkCytoscapeTreeEdges(rootID: string | undefined, action: (edge: SingularElementReturnValue) => void) {
    if (!rootID) {
      return;
    }

    const edges = this.cy.elements(`edge[source = "${rootID}"]`);
    edges.forEach(action);

    edges.forEach(edge => {
      const targetID = edge.target().id();
      this.walkCytoscapeTreeEdges(targetID, action);
    });
  }

  private buildStyle(): StylesheetJson {
    const primary = this.primaryColor;
    return [
      {
        selector: 'node',
        style: {
          'background-color': primary,
          label: 'data(label)',
          height: 40,
          shape: 'round-rectangle',
          color: '#fff'
        }
      },
      {
        selector: '.selected-node',
        style: {
          'underlay-color': '#000000',
          'underlay-padding': 10,
          'underlay-opacity': 0.25
        }
      },
      {
        selector: '.center-center',
        style: {
          'text-valign': 'center',
          'text-halign': 'center'
        }
      },
      {
        selector: 'edge.round-taxi',
        style: {
          'curve-style': 'round-taxi',
          'taxi-direction': 'downward',
          'taxi-turn': 20,
          'taxi-turn-min-distance': 5,
          'taxi-radius': 10,
          'target-arrow-shape': 'triangle',
          'target-arrow-color': 'lightgray',
          'line-color': 'lightgray',
          'color': '#000'
        }
      },
      {
        selector: '.highlighted-edge',
        style: {
          'width': 5,
          'line-color': primary,
          'target-arrow-color': primary
        }
      }
    ] as StylesheetJson;
  }

  private getLabelWidth(node: any) {
    const ctx = document.createElement('canvas').getContext("2d");
    const fStyle = node.pstyle('font-style').strValue;
    const size = node.pstyle('font-size').pfValue + 'px';
    const family = node.pstyle('font-family').strValue;
    const weight = node.pstyle('font-weight').strValue;
    const label = node.data('label');

    if (!ctx) {
      return label.length;
    }

    ctx.font = fStyle + ' ' + weight + ' ' + size + ' ' + family;
    return (ctx?.measureText(label).width ?? label.length) + 20;
  }

  protected getNodeLabel(node: UboTreeNode | undefined, showDateOfBirth = false, showPercentage = false, showBusNum = false) {
    let result = '';
    const party = node?.party;

    if (party) {
      if (party.partyTypeID === ePartyType.individualShareholder) {
        if (party.firstName) {
          result += party.firstName;
        }

        if (party.middleName) {
          result += ` ${party.middleName}`;
        }

        if (party.lastName) {
          result += ` ${party.lastName}`;
        }

        if (party.dateOfBirth && showDateOfBirth) {
          result += ` (${this.datePipe.transform(party.dateOfBirth, 'yyyy-MM-dd')})`
        }
      } else {
        if (party.busName) {
          result += ` ${party.busName}`;
        }

        if (party.busNumber) {
          if(!party.busName) {
            result += ` ${party.busNumber}`;
          } else if(showBusNum) {
            result += ` (${party.busNumber})`;
          }
        }
      }
    }

    if (node?.children.length === 0 &&
      showPercentage) {
      result += ` (${node.ownershipPercentageOfRoot?.toFixed(2)}%)`;
    }

    return result.trim();
  }

  getFinalOwnershipRecords(levels: UboOwnershipLevel[]): UboOwnershipRecord[] {
    this.levels = levels.sort((a, b) => a.level - b.level);

    const intermediateUltimateRecords = levels
      .flatMap(level => level.ownershipRecords)
      .filter(record => record.isUltimate);
    const incompleteLevels = levels
      .filter(level => level.hasDeclaredDoNotKnowMoreShareholders ?? false);

    const finalRecords: UboOwnershipRecord[] = [];

    for (const level of incompleteLevels) {
      const record = levels
        .flatMap(lvl => lvl.ownershipRecords)
        .find(record => record.name.toLowerCase() === level.name.toLowerCase());

      if (record?.ultimatePercentage) {
        record.ultimatePercentage = 100 * (0.01 * record.ultimatePercentage * (1 - level.ownershipRecords.map(r => r.percentage ?? 0).reduce((sum, x) => sum + (0.01 * x), 0)));
        intermediateUltimateRecords.push(record);
      }
    }

    for (const record of intermediateUltimateRecords) {
      const finalRecord = finalRecords.find(r => r.name.toLowerCase() === record.name.toLowerCase() &&
          r.dateOfBirth === record.dateOfBirth);

      if (!finalRecord) {
        finalRecords.push({
          name: record.name,
          isBusiness: record.isBusiness,
          dateOfBirth: record.dateOfBirth,
          number: record.number,
          percentage: record.ultimatePercentage
        });
      } else if (finalRecord.percentage !== undefined) {
        finalRecord.percentage += record.ultimatePercentage ?? 0;
      }
    }

    this.outstandingOwnership = 100 - finalRecords
        .filter(r => !r.isBusiness)
        .reduce((sum, r) => sum + (r.percentage ?? 0), 0);

    return finalRecords.sort((a, b) => (b.percentage ?? 0) - (a.percentage ?? 0));
  }

  protected viewOrder(orderID: string, newTab: boolean = true) {
    if (newTab) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['orders', orderID])
      );

      window.open(url, '_blank');
    } else {
      this.router.navigate(['orders', orderID]);
    }
  }

  protected hasRegistryDocument(order: Order): boolean {
    const result = this.searchResults.get(order.id);
    return !!result?.document;
  }

  protected hasRegHubDocument(order: Order): boolean {
    return order.jurisdiction != 'NS' && order.jurisdiction != 'PE' && !!this.searchResults.get(order.id)?.summaryDocument;
  }

  protected openRegistryDocument(order: Order) {
    this.searchResultsRepo.getSearchResultDocument(order.id)
      .subscribe(result => {
        var mimeType = "application/pdf";

        switch (result.documentTypeID) {
          case "Text":
            mimeType = "text/plain";
            break;
          default:
            mimeType = "application/pdf";
        }

        const blob = base64StringToBlob(result.base64, mimeType);
        const blobURL = URL.createObjectURL(blob);
        window.open(blobURL, '_blank');
      });
  }

  protected openRegHubDocument(order: Order) {
    this.searchResultsRepo.getSearchResultSummaryDocument(order.id)
      .subscribe(result => {
        const blob = base64StringToBlob(result.base64, "application/pdf");
        const blobURL = URL.createObjectURL(blob);
        window.open(blobURL, '_blank');
      });
  }

  protected getUboTreeNodeByID(nodeID: string | null | undefined): UboTreeNode | undefined {
    if (!nodeID || !this.currentRoot) {
      return;
    }

    return this.getUboTreeNode(nodeID, this.currentRoot);
  }

  protected getUboTreeNode(nodeID: string, root: UboTreeNode): UboTreeNode | undefined {
    if(root.id === nodeID) {
      return root;
    }

    for (let i = 0; i < root.children?.length; i++) {
      const child = root.children.at(i)!;
      const result = this.getUboTreeNode(nodeID, child);

      if (result) {
        return result;
      }
    }

    return undefined;
  }

  protected getPepsAndSanctionsPartiesUiConfiguration(): PartyUIConfiguration {
    return {
      autoCapitalizeFields: false,
      showEstate: false,
      showGeneration: false,
      showRegistryCode: false,
      showDateOfBirth: true,
      showPEPS: true,
      showSanctions: true,
      hasContactDetails: false,
      contactDetailsConfiguration: null,
      allowAddingPartyLinks: false,
      partyLinkAdditionTerm: '',
      allowDeletingOldPartyLinks: false,
      allowDeletingNewPartyLinks: false,
      partyLinkDeletionTerm: '',
      showRegistrationNumber: false,
      showJobTitle: false,
      individualOnly: false,
      hidePartyNumber: false,
      hidePartyType: false
    };
  }

  protected scroll() {
    this.ordersRepo.scroll$.next();
  }

  protected getOrderStatus(status: eOrderStatusType | string | null | undefined) {
    if (!status) {
      return '';
    }

    return this.enumLabels.label('orderStatusType', status);
  }  

  protected orderBusinessSearch(tab: UboTreeNodeTab | undefined) {
    if (!tab?.node || tab.businessSearchOrder) {
      return;
    }

    if (tab.node.businessCountry !== 'CA' && tab.node.businessCountry !== 'US')
    {
      return;
    }

    const rootBusinessReportOrder = this.currentRoot?.orders.find(o => o.orderTypeID === eOrderType.businessReport);

    var order = {
      referenceNumber: rootBusinessReportOrder?.referenceNumber ?? '',
      uboTreeNodeID: tab.node.id,
      orderTypeID: eOrderType.businessSearch,
      orderGroupID: rootBusinessReportOrder?.orderGroupID,
      country: tab.node.businessCountry,
      jurisdiction: '_M',
      businessSearchCriteria: {
        name: tab.node.party.busName,
        businessSearchCriteriaTypeID: eBusinessSearchCriteriaTypeString.Name
      },
      searchParameters: {
        searchJurisdictions: [
          { jurisdiction: 'BC' },
          { jurisdiction: 'AB' },
          { jurisdiction: 'SK' },
          { jurisdiction: 'MB' },
          { jurisdiction: 'ON' },
          { jurisdiction: 'QC' },
          { jurisdiction: '_F' },
          { jurisdiction: 'YT' },
          { jurisdiction: 'NT' },
          { jurisdiction: 'NU' },
          { jurisdiction: 'NL' },
          { jurisdiction: 'NB' },
          { jurisdiction: 'NS' },
          { jurisdiction: 'PE' },
        ]
      }
    };

    if (tab.node.businessCountry === 'US') {
      order.searchParameters.searchJurisdictions = [];
      if (tab.node.businessJurisdiction) {
        order.jurisdiction = tab.node.businessJurisdiction;
      }
      else {
        order.jurisdiction = tab.node.party?.contactDetails?.address?.jurisdiction ?? '';
      }
    }

    if (tab.node.isValidated &&
      tab.node.party.partyTypeID === ePartyType.businessShareholder &&
      tab.node.party.busNumber) {
      (order.businessSearchCriteria as any).attestorSelectedNumber = tab.node.party.busNumber;
    }

    this.ordersRepo.createNewOrder(order).subscribe(result => {
      if (result.resource.id) {
        this.router.navigate(['orders', 'business', result.resource.id]);
      }
    });
  }
  
  protected orderBusinessReport(tab: UboTreeNodeTab | undefined) {
    if (!tab?.node) {
      return;
    }

    const rootBusinessReportOrder = this.currentRoot?.orders.find(o => o.orderTypeID === eOrderType.businessReport);

    const order = {
      referenceNumber: rootBusinessReportOrder?.referenceNumber ?? '',
      uboTreeNodeID: tab.node.id,
      orderGroupID: rootBusinessReportOrder?.orderGroupID,
      country: tab.node.businessCountry,
      jurisdiction: tab.node.businessJurisdiction,
      orderTypeID: eOrderType.businessReport,
      businessReportCriteria: {
        number: tab.node.party.busNumber,
        businessName: tab.node.party.busName
      }
    };

    this.ordersRepo.createNewOrder(order)
      .subscribe(result => {
        if (result.resource.id) {
          this.router.navigate(['orders', 'business', result.resource.id]);
        }
      });
  }

  protected orderUbo(tab: UboTreeNodeTab | undefined) {
    if(!tab) {
      return;
    }

    const previousUboMinimal = tab.node.parent?.orders.find(o => o.orderTypeID === eOrderType.ultimateBeneficialOwnershipReport);
    const previousUbo = this.orders.find(o => !!previousUboMinimal && o.id === previousUboMinimal.id);

    const dialogRef = this.dialog.open(UltimateBeneficialOwnershipDialogComponent, {
      data: { node: tab.node, entity: this.businessReportResults.get(tab?.businessReportOrder?.id ?? '')?.value, previousUboOrder: previousUbo, businessReportOrder: tab?.businessReportOrder },
      minWidth: '500px',
      maxWidth: '500px'
    });
  }

  protected shouldDisableBusinessReportButton(tab: UboTreeNodeTab) {
    return !tab.node.party.busNumber ||
      (tab.node.businessCountry === 'US' && !tab.businessSearchOrder) ||
      tab.businessReportOrder ||
      tab.node.isUnregisteredEntity ||
      !tab.node.isValidated ||
      (tab.businessSearchOrder &&
      tab.businessSearchOrder.orderStatusTypeID === eOrderStatusType.complete);
  }

  protected shouldShowOwnershipSummary() {
    return (this.currentRoot?.children.length ?? 0) > 0 ||
      this.orders.some(o => o.orderTypeID === eOrderType.ultimateBeneficialOwnershipReport);
  }

  protected getPepsAndSanctionsOrder() {
    return this.orders.find(o => o.orderTypeID === eOrderType.pepsAndSanctions &&
        o.orderStatusTypeID === eOrderStatusType.draft) ??
      this.orders.find(o => o.orderTypeID === eOrderType.pepsAndSanctions) ??
      this.orders.at(0)!;
  }

  protected regenerateDocument() {
    if (this.hubReportID) {
      this.hubReportService.generateDocument(this.hubReportID).subscribe();
    }
  }

  protected getTypeLabel(node: UboTreeNode) {
    if (node.unregisteredEntityOwnerType) {
      const key = `enums.unregisteredEntityOwnerType.${node.unregisteredEntityOwnerType}`;
      const translated = this.transloco.translate(key);
      return (translated !== key ? translated : Utilities.spacePascalCase(node.unregisteredEntityOwnerType)).toLowerCase();
    } else {
      return this.transloco.translate('enums.unregisteredEntityOwnerType.Shareholder').toLowerCase();
    }
  }

  protected doesDownstreamShareholderHaveUBO(node: UboTreeNode) {
    for (let child of node.children.filter(c => c.party.partyTypeID === ePartyType.businessShareholder)) {
      if (child.orders.some(o => o.orderTypeID === eOrderType.ultimateBeneficialOwnershipReport &&
          o.orderStatusTypeID !== eOrderStatusType.cancelled) ||
        this.doesDownstreamShareholderHaveUBO(child)) {
          return true;
      }
    }

    return false;
  }
}